import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications, LocalNotificationSchema } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notifications";
import router from "@/router";

// Global flag to track if we are ready (prevents race conditions)
let isFCMReady = false;

export const fcm = {
    async register(): Promise<void> {
        const device = Capacitor.getPlatform();
        if (device === "web") {
            console.debug("web platform, no FCM");
            return;
        }

        // Reset flag on new register attempt
        isFCMReady = false;

        console.info("Initializing FCM Listeners...");

        // 1. Initialize Listeners FIRST so we don't miss the 'registration' event
        await this.initializeFCMListeners();

        // 2. Request Permissions
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
            console.info("Push notifications permission status: " + JSON.stringify(permStatus));
        }

        // 3. If granted, Register with Apple/Google
        // This will trigger the 'registration' event defined in initializeFCMListeners
        if (permStatus.receive === 'granted') {
            await PushNotifications.register();
        } else {
            console.warn("User denied push notifications");
        }
    },

    initializeFCMListeners: async function (): Promise<void> {
        const notificationStore = useNotificationStore();

        // Remove old listeners to prevent duplicates if register() is called multiple times
        await PushNotifications.removeAllListeners();

        // --- CRITICAL LISTENER: REGISTRATION ---
        await PushNotifications.addListener('registration', async (token) => {
            console.info(`Push Registration Token received: ${token.value}`);

            // ⚡️ FCM TOKEN SYNC: Wait for FCM token to be ready (especially on new installs)
            try {
                const fcmToken = await FCM.getToken();
                console.info(`FCM Token synchronized: ${fcmToken.token}`);
            } catch (err) {
                console.warn("FCM Token sync failed or delayed, but continuing with registration attempt:", err);
            }

            // ✅ MARK AS READY: Only now is it safe to talk to FCM
            isFCMReady = true;

            // Now we can safely subscribe
            await this.subscribeToTopics();
        });

        // Registration Error
        await PushNotifications.addListener("registrationError", (error) => {
            console.error(`Error on register push-notification: ${JSON.stringify(error)}`);
            isFCMReady = false;
        });

        // Listen for FCM Notification received while app was open
        await PushNotifications.addListener(
            "pushNotificationReceived",
            async (notification) => {
                const not = {
                    notifications: [{
                        id: Date.now(),
                        body: notification.body,
                        title: notification.title,
                        ongoing: false,
                    }]
                };
                // Schedule the Local Notification (Restored)
                const result = await LocalNotifications.schedule(not);
                console.debug("Local Notification scheduled:", result);

                console.log(`Received notification while app open: ${JSON.stringify(notification)}`);

                // Refresh the notifications array in app
                await notificationStore.getAllNotifications();
            }
        );

        // Listen for FCM notification is clicked outside the app
        await PushNotifications.addListener(
            "pushNotificationActionPerformed",
            async (notification) => {
                console.log(`FCM notification clicked and opened app succeeded. content: ${JSON.stringify(notification)}`);
                // Refresh the notifications array in app
                await notificationStore.getAllNotifications();

                // DEVAMS-2645: tapping a car-checklist push notification while the
                // app was closed/backgrounded should open the checklist directly,
                // instead of just landing on whatever screen was last open.
                const checklistId = notification?.notification?.data?.carChecklistId;
                if (checklistId) {
                    router.push(`/tooling/checklist/${checklistId}`);
                }
            }
        );
    },

    // Helper function: Only called when 'registration' event fires
    subscribeToTopics: async function(): Promise<void> {
        if (!isFCMReady) return;

        const authStore = useAuthStore();
        const username = authStore.getUsersign;

        // 1. Subscribe to 'all'
        try {
            await FCM.subscribeTo({ topic: "all" });
            console.info(`✅ [FCM] Successfully subscribed to channel: 'all'`);
        } catch (err) {
            console.error(`❌ [FCM] Failed to subscribe to channel 'all':`, err);
        }

        // 2. Subscribe to specific user topic (isolated try/catch so it runs even if 'all' fails)
        if (username) {
            try {
                await FCM.subscribeTo({ topic: username });
                console.info(`✅ [FCM] Successfully subscribed to channel: '${username}'`);
            } catch (err) {
                console.error(`❌ [FCM] Failed to subscribe to channel '${username}':`, err);
            }
        }
    },

    unsubscribeFromFCM: async function (): Promise<void> {
        const device = Capacitor.getPlatform();
        if (device === "web") {
            console.debug("web platform, no FCM logout required");
            return;
        }

        // ✅ CRITICAL FIX: Don't unsubscribe if we never finished registering!
        // This prevents the "APNS device token not set" crash on fast logout.
        if (!isFCMReady) {
            console.warn("Skipping unsubscribe: FCM was not fully registered yet.");
            return;
        }

        const authStore = useAuthStore();
        const username = authStore.getUsersign;

        try {
            if (username) {
                await FCM.unsubscribeFrom({ topic: username });
                console.info(`Unsubscribed from FCM topic: ${username}`);
            }
            await FCM.unsubscribeFrom({ topic: "all" });
            console.info(`Unsubscribed from FCM topic: all`);
        } catch (err) {
            console.warn("Unsubscribe warning (safe to ignore if logging out):", err);
        }

        // Reset flag
        isFCMReady = false;
    },

    subscribeToTestFCMChannel: async function (val: boolean): Promise<void> {
        const device = Capacitor.getPlatform();
        if(device !== "web") {
            // Note: We should probably check isFCMReady here too, but it's less critical for test functions
            try {
                if(val === true) {
                    await FCM.subscribeTo({topic: "test"});
                    console.info(`Subscribed to FCM topic: test`);
                } else {
                    await FCM.unsubscribeFrom({topic: "test"});
                    console.info(`Unsubscribed from FCM topic: test`);
                }
            } catch (err) {
                console.error(err);
            }
        }
    },

    // KNOWN/ACCEPTED (DEVAMS-2755): on browsers without the Web Notifications API (e.g. iPadOS Edge/Safari web build, not the native iOS app)
    // requestPermissions() rejects with "Notifications not supported in this browser". It surfaces as an unhandled rejection (Sentry FRONTEND-DH)
    // because this is called fire-and-forget from the eventbus handler in src/events/index.ts. Harmless (no functional impact) and deliberately
    // left unfixed: company policy is to use the native iOS app, not the web version on iPad.
    // TODO when this file is next touched: guard with Capacitor.isPluginAvailable('LocalNotifications') and/or try/catch, and add .catch on the
    // eventbus handlers (scheduleNotification, removePushNotification).
    scheduleNotification: async function (data: LocalNotificationSchema[]): Promise<void> {
        const canSend = await LocalNotifications.requestPermissions();
        if (canSend.receive === 'granted') {
            await LocalNotifications.schedule({
                notifications: data
            });
        }
    },

    // KNOWN/ACCEPTED (DEVAMS-2755): same unsupported-web-notifications rejection applies here, see note above scheduleNotification.
    removePushNotification: async function(notification: { id: string | number }): Promise<void> {
        const canSend = await LocalNotifications.requestPermissions();
        if (canSend.receive === 'granted') {
            const pending = await LocalNotifications.getDeliveredNotifications();
            pending.notifications.forEach(pushNotification => {
                if(notification.id === pushNotification.id) {
                    // Note: 'deletedItems' should be an object structure for removeDeliveredNotifications
                    // Fixing your array usage to standard object usage:
                    const itemsToRemove = {
                        notifications: [pushNotification]
                    };
                    LocalNotifications.removeDeliveredNotifications(itemsToRemove);
                }
            });
        }
    },
};

export default fcm;
